import React, { useEffect, useRef, useState } from 'react';

// Mermaid is a large library and only the help pages need it, so it is pulled
// in on first render via dynamic import rather than bundled into the main
// chunk. The promise is cached at module level: every diagram on a page shares
// one load, and initialize() runs exactly once.
let mermaidPromise = null;

const loadMermaid = () => {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({
        startOnLoad: false,
        // 'strict' sanitises the rendered SVG, which matters because the
        // diagram source is stored in the database and injected with
        // dangerouslySetInnerHTML below.
        securityLevel: 'strict',
        // Without this mermaid appends its own error <svg> to document.body on
        // a parse failure -- we render our own message instead.
        suppressErrorRendering: true,
        theme: 'base',
        fontFamily: "'Inter', sans-serif",
        fontSize: 16,
        sequence: {
          // Left on, so the diagram scales to its container. Readability comes
          // from giving it a wider container than the text column (see
          // .mermaid-wrap) rather than from rendering at natural size --
          // natural size on a diagram this wide overflows the page instead of
          // scrolling, because the surrounding section clips it.
          useMaxWidth:       true,
          actorFontSize:     15,
          actorFontWeight:   600,
          messageFontSize:   14,
          noteFontSize:      14,
          actorFontFamily:   "'Inter', sans-serif",
          messageFontFamily: "'Inter', sans-serif",
          noteFontFamily:    "'Inter', sans-serif",
          actorMargin:       60,
          boxMargin:         12,
        },
        flowchart: { useMaxWidth: true },
        themeVariables: {
          background:        '#FFFFFF',
          primaryColor:      '#F0F2F5',
          primaryTextColor:  '#1A1F2E',
          primaryBorderColor:'#C8CDD6',
          lineColor:         '#6B7280',
          textColor:         '#1A1F2E',
          actorBkg:          '#F0F2F5',
          actorBorder:       '#1B2A4A',
          actorTextColor:    '#1A1F2E',
          signalColor:       '#1B2A4A',
          signalTextColor:   '#1A1F2E',
          labelBoxBkgColor:  '#FEF3C7',
          labelBoxBorderColor:'#B07820',
        },
      });
      return mermaid;
    });
  }
  return mermaidPromise;
};

// Validates diagram source without rendering it. Used by the editor so a
// broken diagram can be rejected on save rather than stored and discovered
// later by whoever opens the page.
export const validateMermaid = async (code) => {
  if (!code || !code.trim()) return null;
  try {
    const mermaid = await loadMermaid();
    await mermaid.parse(code);
    return null;
  } catch (e) {
    return e?.message || String(e);
  }
};

let renderSeq = 0;

const MermaidDiagram = ({ code }) => {
  const [svg, setSvg]     = useState('');
  const [error, setError] = useState(null);
  const mounted           = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!code || !code.trim()) { setSvg(''); setError(null); return; }

    let cancelled = false;
    (async () => {
      try {
        const mermaid = await loadMermaid();
        await mermaid.parse(code);
        // Each render needs its own id -- mermaid keys internal state on it
        // and reusing one across re-renders produces empty output.
        const { svg: out } = await mermaid.render(`mermaid-${++renderSeq}`, code);
        if (!cancelled && mounted.current) { setSvg(out); setError(null); }
      } catch (e) {
        // The previous good SVG is deliberately left on screen: a typo mid-edit
        // should not blank out the diagram someone was reading.
        if (!cancelled && mounted.current) setError(e?.message || String(e));
      }
    })();

    return () => { cancelled = true; };
  }, [code]);

  if (!code || !code.trim()) return null;

  return (
    <div className="mermaid-wrap">
      {error && <div className="mermaid-error">Diagram error — {error}</div>}
      {svg
        ? <div className="mermaid-svg" dangerouslySetInnerHTML={{ __html: svg }} />
        : !error && <div className="mermaid-loading">Rendering diagram…</div>}
    </div>
  );
};

export default MermaidDiagram;
